import { useCallback, useEffect, useState } from 'react'
import { serviceWorkerRegistration } from './register'

/**
 * Noticing when a new build is installed and waiting to take over.
 *
 * **The worker never takes over on its own.** A `skipWaiting()` in the worker itself would
 * swap the running JavaScript under someone halfway through editing a scene — the update
 * would be instant and the half-typed aspect would be gone. So the new worker waits, this
 * hook notices, and the app offers a reload the user presses when they are ready.
 *
 * The registration itself happens in `register.ts`, at app start and outside the auth
 * guard; this only observes it.
 */
export interface ServiceWorkerState {
  /** A new version is installed and waiting for permission to take over. */
  updateReady: boolean
  /** Activates the waiting worker and reloads. Only meaningful when `updateReady`. */
  update: () => void
}

const SKIP_WAITING = 'harrowgate:skip-waiting'

export function useServiceWorker(): ServiceWorkerState {
  const [waiting, setWaiting] = useState<ServiceWorker | null>(null)

  useEffect(() => {
    let cancelled = false

    void serviceWorkerRegistration().then((registration) => {
      if (registration === null || cancelled) return

      // Already waiting when this mounted — a second tab installed it, or the user
      // dismissed the prompt last time and has come back.
      if (registration.waiting !== null) setWaiting(registration.waiting)

      registration.addEventListener('updatefound', () => {
        const installing = registration.installing
        if (installing === null) return

        installing.addEventListener('statechange', () => {
          // `controller` distinguishes an *update* from a first install: with no
          // controller this is the first worker this page has ever had, and there is
          // nothing to interrupt and nothing to ask about.
          if (installing.state === 'installed' && navigator.serviceWorker.controller !== null) {
            setWaiting(installing)
          }
        })
      })
    })

    return () => {
      cancelled = true
    }
  }, [])

  const update = useCallback(() => {
    if (waiting === null) return

    // The reload is driven by `controllerchange` rather than fired straight after the
    // message: the new worker has to actually take over first, or the reload races it and
    // loads the old bundle from the old worker.
    navigator.serviceWorker.addEventListener('controllerchange', () => window.location.reload(), {
      once: true,
    })
    waiting.postMessage(SKIP_WAITING)
  }, [waiting])

  return { updateReady: waiting !== null, update }
}

/**
 * Whether the browser thinks it can reach the network.
 *
 * `navigator.onLine` is famously optimistic — it reports the state of the network
 * interface, not of the internet — so this is worth showing as an explanation and never
 * worth acting on. A request that fails while this says "online" is still a failed
 * request, and the API layer's own error is the authority on that.
 */
export function useOnline(): boolean {
  const [online, setOnline] = useState(() => navigator.onLine)

  useEffect(() => {
    const goOnline = () => setOnline(true)
    const goOffline = () => setOnline(false)

    window.addEventListener('online', goOnline)
    window.addEventListener('offline', goOffline)

    return () => {
      window.removeEventListener('online', goOnline)
      window.removeEventListener('offline', goOffline)
    }
  }, [])

  return online
}
